import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { HarmonyEdit } from "@/components/icons/harmony-icons-inline";
import { cn } from "@/lib/utils";
import { useLeftSidebar } from "@/components/ui/sidebar";
import { useSidebarConversations } from "@/features/conversations/hooks/use-sidebar-conversations";
import { useTemporaryChat } from "@/features/temporary-chat";
import { isMobile } from "@/lib/is-mobile";
import { isModifiedClick } from "@/lib/link-utils";
import { SidebarTooltip } from "./SidebarTooltip";

const ICON_SIZE = 18;

export default function StartNewChatButton() {
  const { t } = useTranslation();
  const { open: isSidebarOpen, setOpen: setSidebarOpen } = useLeftSidebar();
  const { newChatPath } = useSidebarConversations();
  const { handleReset } = useTemporaryChat();

  // 🔗 Navigasyonu <Link> üstleniyor; burada sadece yan etkiler kalıyor
  // (temporary chat state reset + mobilde sidebar kapatma). Modifier'lı
  // tıklamada (yeni sekme) sayfa değişmiyor, o yüzden guard'lı çalışıyor.
  const handleNewChatSideEffects = (e: React.MouseEvent) => {
    if (isModifiedClick(e)) return;
    handleReset();
    if (isMobile()) {
      setSidebarOpen(false);
    }
  };

  return (
    <div className={cn(isSidebarOpen ? "px-2" : "flex justify-center")}>
      <SidebarTooltip label={t("chat.newChat")} disabled={isSidebarOpen}>
        <Link
          to={newChatPath}
          onClick={handleNewChatSideEffects}
          className={cn(
            "group relative isolate flex items-center justify-center gap-2 overflow-hidden rounded-full text-white",
            // Referans .pill: 1.5px cam kenar + hafif blur
            "border-[1.5px] border-white/30 backdrop-blur-[8px]",
            // .pill.three — 105deg mor gradyan
            "bg-[linear-gradient(105deg,#8351e8_0%,#704de3_48%,#a45ae4_100%)]",
            // Dış parlama + inset highlight (kaynak box-shadow)
            "shadow-[0_12px_24px_rgba(82,96,180,0.20),0_0_25px_rgba(143,74,246,0.43),inset_0_1px_0_rgba(255,255,255,0.32),inset_0_-1px_0_rgba(255,255,255,0.14)]",
            "hover:shadow-[0_12px_24px_rgba(82,96,180,0.28),0_0_32px_rgba(143,74,246,0.55),inset_0_1px_0_rgba(255,255,255,0.40),inset_0_-1px_0_rgba(255,255,255,0.14)]",
            "transition-shadow duration-200 active:scale-[0.98] focus:outline-none",
            isSidebarOpen ? "w-full h-11 px-4" : "size-10",
          )}
        >
          {/* .pill::before — 100deg cam sheen */}
          <span
            aria-hidden
            className="pointer-events-none absolute inset-0 rounded-full bg-[linear-gradient(100deg,rgba(255,255,255,0.14),rgba(255,255,255,0)_38%,rgba(255,255,255,0.08))]"
          />

          <HarmonyEdit
            size={ICON_SIZE}
            className="relative z-10 text-white shrink-0 [filter:drop-shadow(0_1px_2px_rgba(45,30,100,0.16))]"
          />
          {isSidebarOpen && (
            <span className="relative z-10 text-sm font-medium tracking-tight [text-shadow:0_1px_2px_rgba(45,30,100,0.16)]">
              {t("chat.newChat")}
            </span>
          )}
        </Link>
      </SidebarTooltip>
    </div>
  );
}
